// AdminMarginMonitorPage — Real-time view of accounts at >25% margin
// Section 13.13 enforcement, sortable list

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface MarginAcct {
  id: string;
  trader: string;
  avatar: number;
  account: string;
  tier: 'Beginner' | 'Mid' | 'Professional';
  accountSize: number;
  balance: number;
  marginPct: number;
  positions: number;
  status: 'safe' | 'warning' | 'breach';
  lastUpdate: string;
}

const ACCOUNTS: MarginAcct[] = [
  { id: 'a1', trader: 'Marcus J.', avatar: 1, account: 'FP-100K-14283', tier: 'Mid',          accountSize: 100000, balance: 102480, marginPct: 11, positions: 3, status: 'safe',     lastUpdate: '12s ago' },
  { id: 'a2', trader: 'Sarah C.',  avatar: 2, account: 'FP-150K-12844', tier: 'Professional', accountSize: 150000, balance: 154280, marginPct: 14, positions: 5, status: 'safe',     lastUpdate: '8s ago' },
  { id: 'a3', trader: 'Riley T.',  avatar: 3, account: 'FP-50K-15201',  tier: 'Beginner',     accountSize: 50000,  balance: 50320,  marginPct: 26, positions: 2, status: 'warning',  lastUpdate: '15s ago' },
  { id: 'a4', trader: 'Devon K.',  avatar: 4, account: 'FP-100K-13892', tier: 'Mid',          accountSize: 100000, balance: 100680, marginPct: 32, positions: 4, status: 'breach',   lastUpdate: '4s ago' },
  { id: 'a5', trader: 'Felix W.',  avatar: 1, account: 'FP-50K-14672',  tier: 'Beginner',     accountSize: 50000,  balance: 49880,  marginPct: 28, positions: 2, status: 'warning',  lastUpdate: '22s ago' },
  { id: 'a6', trader: 'Ana M.',    avatar: 2, account: 'FP-150K-13256', tier: 'Professional', accountSize: 150000, balance: 151840, marginPct: 18, positions: 5, status: 'safe',     lastUpdate: '5s ago' },
  { id: 'a7', trader: 'Brent O.',  avatar: 3, account: 'FP-100K-14821', tier: 'Mid',          accountSize: 100000, balance: 100420, marginPct: 27, positions: 3, status: 'warning',  lastUpdate: '11s ago' },
  { id: 'a8', trader: 'Alex P.',   avatar: 4, account: 'FP-150K-14201', tier: 'Professional', accountSize: 150000, balance: 148200, marginPct: 34, positions: 6, status: 'breach',   lastUpdate: '2s ago' },
];

const STATUS_META: Record<string, { color: string; label: string }> = {
  safe:     { color: '#34C759', label: 'Safe' },
  warning:  { color: '#FF9500', label: 'Warning' },
  breach:   { color: '#FF3B30', label: 'Breach' },
};
const TIER_COLOR = { Beginner: '#34C759', Mid: '#0A84FF', Professional: '#1C1C1E' };

export function AdminMarginMonitorPage() {
  const nav = useNavigate();
  const [accounts, setAccounts] = useState(ACCOUNTS);
  const [sortBy, setSortBy] = useState<'margin' | 'name' | 'balance'>('margin');

  const sorted = [...accounts].sort((a, b) => {
    if (sortBy === 'margin') return b.marginPct - a.marginPct;
    if (sortBy === 'balance') return b.balance - a.balance;
    return a.trader.localeCompare(b.trader);
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl px-2">
        <button onClick={() => nav('/admin/dashboard')} data-testid="back" className="flex h-9 w-9 items-center justify-center text-[#0A84FF] haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Margin monitor</div>
        <div className="w-9" />
      </div>

      {/* Hero */}
      <div className="mx-4 mt-3 rounded-[16px] border border-white/10 bg-gradient-to-br from-[#FF3B30] to-[#B82B22] p-4 text-center">
        <div className="text-[11px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.85)' }}>Section 13.13 · 30% margin limit</div>
        <div className="mt-1 text-[24px] font-bold">{accounts.filter((a) => a.status === 'breach').length} accounts over limit</div>
        <div className="text-[12px]" style={{ color: 'rgba(255,255,255,0.85)' }}>{accounts.filter((a) => a.status === 'warning').length} approaching</div>
      </div>

      {/* Sort */}
      <div className="px-4 pt-3 flex items-center gap-1.5">
        <span className="text-[10px] text-white/50 uppercase">Sort by</span>
        {(['margin', 'name', 'balance'] as const).map((s) => (
          <button key={s} onClick={() => setSortBy(s)} data-testid={`sort-${s}`}
            className={['shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize transition',
              sortBy === s ? 'bg-white text-black' : 'bg-white/10 text-white/70 active:opacity-40'
            ].join(' ')}
          >{s}</button>
        ))}
      </div>

      {/* List */}
      <div className="px-4 mt-3 space-y-1.5">
        {sorted.map((a) => {
          const sm = STATUS_META[a.status];
          return (
            <div key={a.id} data-testid={`margin-${a.id}`} className={['rounded-[12px] border p-2.5',
              a.status === 'breach' ? 'border-[#FF3B30]/50 bg-[#FF3B30]/5' :
              a.status === 'warning' ? 'border-[#FF9500]/30 bg-[#FF9500]/5' :
              'border-white/10 bg-white/5'
            ].join(' ')}>
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-bold" style={{ backgroundColor: TIER_COLOR[a.tier] + '30', color: TIER_COLOR[a.tier] }}>{a.trader[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[12px] font-bold text-white truncate">{a.trader}</span>
                    <span className="rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase" style={{ backgroundColor: sm.color + '20', color: sm.color }}>{sm.label}</span>
                  </div>
                  <div className="text-[10px] text-white/50 font-mono">{a.account} · {a.positions} pos · {a.lastUpdate}</div>
                  <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(a.marginPct * 2.5, 100)}%`, backgroundColor: sm.color }} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[14px] font-bold tabular-nums" style={{ color: sm.color }}>{a.marginPct}%</div>
                  <div className="text-[9px] text-white/40">${a.balance.toLocaleString()}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
